"use client";

import dynamic from "next/dynamic";
import { Component, useEffect, useRef, useState, type ReactNode } from "react";

import type { HomeMarketingFeatureSlug } from "@/content/home-feature-modules";
import { homeFeatureShowcaseVisualScopeClass } from "@/features/home/components/home-feature-showcase/home-feature-showcase.constants";
import { cn } from "@/lib/utils";

const FeatureShowcaseVisual = dynamic(
  () =>
    import("@/features/home/components/home-feature-showcase/FeatureShowcaseVisual").then(
      (module) => module.FeatureShowcaseVisual,
    ),
  { ssr: false },
);

/** Starts loading a demo this far before it scrolls into view. */
const LOAD_AHEAD_MARGIN = "400px 0px";

/** A demo that fails to load (offline, blocked chunk) leaves the poster in place. */
class AppViewBoundary extends Component<
  { fallback: ReactNode; children: ReactNode },
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

type LazyAppViewProps = {
  featureId: HomeMarketingFeatureSlug;
  label: string;
  /** Static drawing shown before the demo loads, and without JavaScript. */
  poster: ReactNode;
};

/**
 * The same step in the app, using the site's existing product demo. It loads
 * only when it nears the viewport, in a fixed-height frame so nothing shifts.
 * The demos show their finished state when reduced motion is on.
 */
export const LazyAppView = ({ featureId, label, poster }: LazyAppViewProps) => {
  const frameRef = useRef<HTMLDivElement>(null);
  const [isNear, setIsNear] = useState(false);

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return;
    if (typeof IntersectionObserver === "undefined") {
      setIsNear(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setIsNear(true);
          observer.disconnect();
        }
      },
      { rootMargin: LOAD_AHEAD_MARGIN },
    );
    observer.observe(frame);
    return () => observer.disconnect();
  }, []);

  const posterFrame = (
    <div className="flex h-full items-center justify-center p-8">
      <div className="w-full max-w-xs">{poster}</div>
    </div>
  );

  return (
    <figure className="flex h-full min-w-0 flex-col gap-3">
      {/* Below the frame on desktop, so the frame and the document share a top edge. */}
      <figcaption className="text-[11px] font-medium uppercase tracking-[0.2em] text-muted-foreground lg:order-last">
        {label}
      </figcaption>
      {/*
       * A size container: the demo hides its side rails when this frame is
       * narrow (home-prep.css). On desktop it grows to the document's height;
       * size containment keeps the demo's own height from stretching the row.
       */}
      <div
        ref={frameRef}
        data-app-view-frame
        className={cn(
          "prep-app-view h-[380px] overflow-hidden rounded-lg border border-border bg-muted/40 sm:h-[440px] lg:h-auto lg:min-h-[440px] lg:flex-1 lg:[contain:size] dark:bg-white/[0.02]",
          homeFeatureShowcaseVisualScopeClass,
        )}
      >
        {isNear ? (
          <AppViewBoundary fallback={posterFrame}>
            <FeatureShowcaseVisual
              featureId={featureId}
              className="h-full min-h-0"
            />
          </AppViewBoundary>
        ) : (
          posterFrame
        )}
      </div>
    </figure>
  );
};
